import {
    type ColorlessIconProps,
    IconMarket,
    IconMoySklad,
    IconOzon,
    IconWb,
    IconYml,
    Link,
    Paragraph,
    ParagraphList,
    Text,
    VStack,
} from '@direct-frontend/components';
import { type OpenApiSchemaIntegrationType } from '#shared/api/schema';
import { OZON_SERVICE_NAME } from '#shared/lib/brand';
import { LINK_YANDEX_MARKET_CABINET, LINK_YANDEX_YML_INSTRUCTIONS } from '#shared/lib/constants';
import { IconOzonLogistics } from '#shared/ui/icons';
import {
    integrationMoySkladSetupAdd,
    integrationMoySkladSetupDelete,
    integrationMoySkladSetupGet,
    integrationOzonSetupAdd,
    integrationOzonSetupDelete,
    integrationOzonSetupGet,
    integrationWildberriesSetupAdd,
    integrationWildberriesSetupDelete,
    integrationWildberriesSetupGet,
    integrationYamlDeleteSetup,
    integrationYamlGetSetup,
    integrationYamlPostSetup,
    integrationYandexMarketSetupDelete,
    integrationYandexMarketSetupGet,
    integrationYandexMarketSetupPost,
    useIntegrationMoySkladSetup,
    useIntegrationOzonSetup,
    useIntegrationWildberriesSetup,
    useIntegrationYamlGetSetup,
    useIntegrationYandexMarketSetup,
} from '../api';
import { type IntegrationDatasourceSystem, type IntegrationSourceItem } from './integrationSourceDataTypes';
import {
    PLACEHOLDER_API_KEY,
    PLACEHOLDER_AUTH_TOKEN,
    PLACEHOLDER_TOKEN,
    PLACEHOLDER_YML_LINK,
} from './placeholderConst';
import styles from './integrationSourceData.module.css';

const OZON_SELLER_CABINET_URL = 'https://seller.ozon.ru';

const ozonSourceData: IntegrationSourceItem = {
    displayName: `${OZON_SERVICE_NAME} Доставка` as const,
    displayNameGenitive: `${OZON_SERVICE_NAME} Доставки` as const,
    displayNamePrepositional: `${OZON_SERVICE_NAME} Доставке` as const,
    credentialRowPlaceholder: PLACEHOLDER_API_KEY,
    additionalFields: [],
    HeaderCaption: <></>,
    LogoIcon: IconOzonLogistics,
    useEndpoint: useIntegrationOzonSetup,
    endpointSetupGet: integrationOzonSetupGet,
    endpointSetupAdd: integrationOzonSetupAdd,
    endpointSetupDelete: integrationOzonSetupDelete,
} as const;

const INTEGRATION_SOURCE_DATA: Record<IntegrationDatasourceSystem, IntegrationSourceItem> = {
    wildberries: {
        displayName: 'Wildberries',
        displayNameGenitive: 'Wildberries',
        displayNamePrepositional: 'Wildberries',
        credentialRowPlaceholder: PLACEHOLDER_API_KEY,
        additionalFields: ['token'],
        HeaderCaption: (
            <ParagraphList
                role="supplementary"
                color="gray"
                view="numbered"
                isCompact
                items={[
                    {
                        content: 'Войдите в ваш аккаунт продавца на Wildberries (WB Партнёры).',
                    },
                    {
                        content: 'Перейдите в Настройки — Доступ к API.',
                    },
                    {
                        content: (
                            <VStack>
                                <Text role="supplementary" color="gray">
                                    Создайте токен для интеграции с помощью API.
                                </Text>
                                <ul className={styles.ul}>
                                    <li>
                                        <Text role="supplementary" color="gray">
                                            При создании токена выберите: Контент, Маркетплейс, Аналитика, Вопросы и
                                            отзывы, Цены и скидки.
                                        </Text>
                                    </li>
                                </ul>
                            </VStack>
                        ),
                    },
                    {
                        content: `Добавьте ${PLACEHOLDER_API_KEY}.`,
                    },
                ]}
            />
        ),
        LogoIcon: (props: ColorlessIconProps) => <IconWb {...props} />,
        useEndpoint: useIntegrationWildberriesSetup,
        endpointSetupGet: integrationWildberriesSetupGet,
        endpointSetupAdd: integrationWildberriesSetupAdd,
        endpointSetupDelete: integrationWildberriesSetupDelete,
    },
    moysklad: {
        displayName: 'МойСклад',
        displayNameGenitive: 'МойСклад',
        displayNamePrepositional: 'МойСкладе',
        credentialRowPlaceholder: PLACEHOLDER_TOKEN,
        additionalFields: ['token'],
        HeaderCaption: (
            <ParagraphList
                role="supplementary"
                color="gray"
                view="numbered"
                isCompact
                items={[
                    {
                        content: 'Войдите в ваш личный кабинет МойСклад.',
                    },
                    {
                        content: 'Перейдите в Настройки — Обмен данными — Токены.',
                    },
                    {
                        content: 'Создайте новый токен, скопируйте его.',
                    },
                    {
                        content: 'Добавьте токен.',
                    },
                ]}
            />
        ),
        LogoIcon: (props: ColorlessIconProps) => <IconMoySklad {...props} />,
        useEndpoint: useIntegrationMoySkladSetup,
        endpointSetupGet: integrationMoySkladSetupGet,
        endpointSetupAdd: integrationMoySkladSetupAdd,
        endpointSetupDelete: integrationMoySkladSetupDelete,
    },
    ozon: {
        displayName: OZON_SERVICE_NAME,
        displayNameGenitive: OZON_SERVICE_NAME,
        displayNamePrepositional: OZON_SERVICE_NAME,
        credentialRowPlaceholder: PLACEHOLDER_API_KEY,
        additionalFields: ['api_key', 'client_id'],
        HeaderCaption: (
            <ParagraphList
                color="gray"
                role="supplementary"
                view="numbered"
                isCompact
                items={[
                    {
                        content: (
                            <Text role="supplementary" color="gray">
                                Войдите в ваш аккаунт{' '}
                                <Link href={OZON_SELLER_CABINET_URL} target="_blank" rel="noreferrer">
                                    продавца на {OZON_SERVICE_NAME}.
                                </Link>
                            </Text>
                        ),
                    },
                    {
                        content: 'Перейдите в Настройки — Seller API.',
                    },
                    {
                        content:
                            'Создайте токен для интеграции с помощью кнопки «Сгенерировать ключ». При создании токена выберите роль Admin.',
                    },
                    {
                        content: `Добавьте ${PLACEHOLDER_API_KEY} и введите в поле ниже.`,
                    },
                ]}
            />
        ),
        LogoIcon: (props: ColorlessIconProps) => <IconOzon {...props} />,
        useEndpoint: useIntegrationOzonSetup,
        endpointSetupGet: integrationOzonSetupGet,
        endpointSetupAdd: integrationOzonSetupAdd,
        endpointSetupDelete: integrationOzonSetupDelete,
    },
    ozon_delivery_fbs: ozonSourceData,
    ozon_fbo_plus: ozonSourceData,
    yml: {
        displayName: 'YML-фид',
        displayNameGenitive: 'YML-фида',
        displayNamePrepositional: 'YML-фиде',
        buttonText: 'Добавить ссылку на YML',
        modalTitleText: 'Ссылка на YML-фид',
        credentialRowPlaceholder: PLACEHOLDER_YML_LINK,
        additionalFields: ['url'],
        HeaderCaption: (
            <Paragraph role="supplementary">
                Для загрузки используйте фид, соответствующий{' '}
                <Link href={LINK_YANDEX_YML_INSTRUCTIONS} target="_blank" rel="noreferrer">
                    стандартному виду Яндекса
                </Link>
            </Paragraph>
        ),
        LogoIcon: (props: ColorlessIconProps) => <IconYml {...props} />,
        useEndpoint: useIntegrationYamlGetSetup,
        endpointSetupGet: integrationYamlGetSetup,
        endpointSetupAdd: integrationYamlPostSetup,
        endpointSetupDelete: integrationYamlDeleteSetup,
    },
    yandex_market: {
        displayName: 'Яндекс Маркет',
        displayNameGenitive: 'Яндекс Маркета',
        displayNamePrepositional: 'Яндекс Маркете',
        credentialRowPlaceholder: PLACEHOLDER_AUTH_TOKEN,
        additionalFields: ['token'],
        HeaderCaption: (
            <ParagraphList
                role="supplementary"
                color="gray"
                view="numbered"
                isCompact
                items={[
                    {
                        content: (
                            <Text role="supplementary" color="gray">
                                Войдите в ваш{' '}
                                <Link href={LINK_YANDEX_MARKET_CABINET} target="_blank" rel="noreferrer">
                                    аккаунт продавца на Яндекс Маркет.
                                </Link>
                            </Text>
                        ),
                    },
                    {
                        content: 'В кабинете перейдите в Настройки → API и модули.',
                    },
                    {
                        content: 'Создайте новый токен. Рекомендуем выбрать «Полное управление кабинетом».',
                    },
                    {
                        content: 'Добавьте токен в поле ниже.',
                    },
                ]}
            />
        ),
        LogoIcon: (props: ColorlessIconProps) => <IconMarket {...props} />,
        useEndpoint: useIntegrationYandexMarketSetup,
        endpointSetupGet: integrationYandexMarketSetupGet,
        endpointSetupAdd: integrationYandexMarketSetupPost,
        endpointSetupDelete: integrationYandexMarketSetupDelete,
    },
    ya_market_laas: {
        displayName: 'Фулфилмент Яндекс Маркета',
        displayNameGenitive: 'Фулфилмента Яндекс Маркета',
        displayNamePrepositional: 'Фулфилменте Яндекс Маркета',
        credentialRowPlaceholder: PLACEHOLDER_AUTH_TOKEN,
        additionalFields: ['token'],
        HeaderCaption: (
            <ParagraphList
                role="supplementary"
                color="gray"
                view="numbered"
                isCompact
                items={[
                    {
                        content: (
                            <Text role="supplementary" color="gray">
                                Войдите в ваш{' '}
                                <Link href={LINK_YANDEX_MARKET_CABINET} target="_blank" rel="noreferrer">
                                    аккаунт продавца на Яндекс Маркет.
                                </Link>
                            </Text>
                        ),
                    },
                    {
                        content: 'В кабинете перейдите в Настройки → API и модули.',
                    },
                    {
                        content: 'Создайте новый токен. Рекомендуем выбрать «Полное управление кабинетом».',
                    },
                    {
                        content: 'Добавьте токен в поле ниже.',
                    },
                ]}
            />
        ),
        LogoIcon: (props: ColorlessIconProps) => <IconMarket {...props} />,
        useEndpoint: useIntegrationYandexMarketSetup,
        endpointSetupGet: integrationYandexMarketSetupGet,
        endpointSetupAdd: integrationYandexMarketSetupPost,
        endpointSetupDelete: integrationYandexMarketSetupDelete,
    },
} as const;

export const getIntegrationSourceData = <T extends AvailableSource>(source: T): (typeof INTEGRATION_SOURCE_DATA)[T] =>
    INTEGRATION_SOURCE_DATA[source];

export type AvailableSource = keyof typeof INTEGRATION_SOURCE_DATA;

export const isAvailableIntegrationSource = (
    integrationType: OpenApiSchemaIntegrationType | null | undefined
): integrationType is AvailableSource => Boolean(integrationType && integrationType in INTEGRATION_SOURCE_DATA);
